// File: src/services/media/registry/podcastMetaV1.ts
//
// Podcast Meta v1 — strict audio mix hints for registry-resolved assets
// --------------------------------------------------------------------
// Goals:
// - Canonical typed shape for podcast audio registry metadata
// - Safe normalization with deterministic defaults
// - Utility helpers to compute render hints (gain, fades, loop)
//
// NOTE: This is "hints", not analysis. We do NOT measure LUFS at runtime here.
// We use metadata loudnessLUFS to compute a deterministic trim relative to target.

import type { ResolvedMediaV1 } from "@/services/media/resolve/resolveMedia";
import { normStr } from "@/services/media/contracts/mediaRefV1";

export type PodcastAudioRoleV1 =
  | "identity"
  | "brand"
  | "transition"
  | "sting"
  | "bed"
  | "underscore"
  | "sfx"
  | "voice"
  | "cta"
  | "system"
  | "censor"
  | "silence";

export type PodcastAudioTagV1 =
  | "intro"
  | "outro"
  | "loop"
  | "soft"
  | "hard"
  | "ambient"
  | "impact"
  | "whoosh"
  | "click"
  | "page"
  | "breath";

export interface PodcastAudioMetaV1 {
  schema: "podcastAudioMetaV1";

  role: PodcastAudioRoleV1;
  loop: boolean;

  /**
   * Declared integrated loudness in LUFS (e.g. -16).
   * Used to compute deterministic trim relative to a target loudness.
   */
  loudnessLUFS: number;

  /** Optional fades (milliseconds). */
  fadeInMs?: number;
  fadeOutMs?: number;

  /**
   * Optional additional gain trim in dB (after LUFS alignment).
   * Positive increases level, negative reduces.
   */
  gainDb?: number;

  /** Optional classification tags (keep small). */
  tags?: PodcastAudioTagV1[];

  /** Optional informational fields (not required). */
  bpm?: number;
  lengthMs?: number;
}

export interface PodcastAudioRenderHintsV1 {
  role: PodcastAudioRoleV1;
  loop: boolean;

  /** Linear gain multiplier (applied to a GainNode). */
  gain: number;

  /** Fade times in seconds. */
  fadeInSec: number;
  fadeOutSec: number;

  /** Debug/context (safe). */
  tags?: string[];
  targetLUFS: number;
  declaredLUFS: number;
  gainDbTotal: number;
}

/* ------------------------------ */
/* Defaults / normalizers         */
/* ------------------------------ */

const ROLE_ENUM = new Set<PodcastAudioRoleV1>([
  "identity",
  "brand",
  "transition",
  "sting",
  "bed",
  "underscore",
  "sfx",
  "voice",
  "cta",
  "system",
  "censor",
  "silence",
]);

function isRole(v: unknown): v is PodcastAudioRoleV1 {
  return typeof v === "string" && ROLE_ENUM.has(v as PodcastAudioRoleV1);
}

function toRole(v: unknown, keyHint = ""): PodcastAudioRoleV1 {
  if (isRole(v)) return v;

  // deterministic inference if missing/invalid
  const k = String(keyHint || "").toLowerCase();
  if (k.includes("censor") || k.includes("beep")) return "censor";
  if (k.includes("silence")) return "silence";
  if (k.startsWith("sfx")) return "sfx";
  if (k.startsWith("voice")) return "voice";
  if (k.includes("cta")) return "cta";
  if (k.includes("bed")) return "bed";
  if (k.includes("sting")) return "sting";
  if (k.includes("transition")) return "transition";
  if (k.includes("intro") || k.includes("outro") || k.includes("ident")) return "identity";
  return "system";
}

function toBool(v: unknown, fallback: boolean): boolean {
  return typeof v === "boolean" ? v : fallback;
}

function toNum(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function clampMs(v: unknown): number | undefined {
  if (v == null) return undefined;
  const n = Math.max(0, toNum(v, 0));
  return n > 0 ? n : undefined;
}

function normalizeTags(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out = v.map((t) => normStr(t, "")).filter(Boolean);
  return out.length ? out : undefined;
}

function defaultLoopForRole(role: PodcastAudioRoleV1): boolean {
  return role === "bed" || role === "underscore" || role === "silence" || role === "system";
}

function defaultLUFSForRole(role: PodcastAudioRoleV1): number {
  // podcast defaults (tweak later if you want)
  if (role === "bed" || role === "underscore") return -20;
  if (role === "sfx" || role === "sting" || role === "transition") return -18;
  // voice/identity/cta/system/silence
  return -16;
}

export function normalizePodcastAudioMetaV1(
  input: unknown,
  opts?: { keyHint?: string },
): PodcastAudioMetaV1 {
  const keyHint = opts?.keyHint || "";
  const d = (input && typeof input === "object" ? (input as any) : {}) as any;

  const schemaOk = d.schema === "podcastAudioMetaV1" ? "podcastAudioMetaV1" : "podcastAudioMetaV1";

  const role = toRole(d.role, keyHint);
  const loop = toBool(d.loop, defaultLoopForRole(role));
  const loudnessLUFS = toNum(d.loudnessLUFS, defaultLUFSForRole(role));

  const fadeInMs = clampMs(d.fadeInMs);
  const fadeOutMs = clampMs(d.fadeOutMs);
  const gainDb = d.gainDb != null ? toNum(d.gainDb, 0) : undefined;

  const tags = normalizeTags(d.tags) as any;

  const bpm = d.bpm != null ? toNum(d.bpm, 0) : undefined;
  const lengthMs = d.lengthMs != null ? Math.max(0, toNum(d.lengthMs, 0)) : undefined;

  return {
    schema: schemaOk,
    role,
    loop,
    loudnessLUFS,
    ...(fadeInMs ? { fadeInMs } : {}),
    ...(fadeOutMs ? { fadeOutMs } : {}),
    ...(gainDb != null && gainDb !== 0 ? { gainDb } : {}),
    ...(tags ? { tags } : {}),
    ...(bpm ? { bpm } : {}),
    ...(lengthMs ? { lengthMs } : {}),
  };
}

/* ------------------------------ */
/* Gain math                       */
/* ------------------------------ */

export function dbToLinear(db: number): number {
  // linear = 10^(db/20)
  return Math.pow(10, db / 20);
}

/**
 * Compute deterministic gain from declared LUFS and optional gainDb.
 * gainDbTotal = (targetLUFS - declaredLUFS) + gainDb
 */
export function computeGainFromMeta(
  meta: PodcastAudioMetaV1,
  targetLUFS = -16,
): { gain: number; gainDbTotal: number } {
  const declared = Number(meta.loudnessLUFS);
  const trimDb = targetLUFS - declared;
  const extraDb = meta.gainDb != null ? Number(meta.gainDb) : 0;
  const gainDbTotal = trimDb + extraDb;
  const gain = dbToLinear(gainDbTotal);
  return { gain, gainDbTotal };
}

/* ------------------------------ */
/* Extract from ResolvedMediaV1    */
/* ------------------------------ */

type RegistryDocMetaSlot =
  | { meta?: unknown; key?: string; registry?: string | null }
  | null
  | undefined;

/**
 * We store the registry doc payload in ResolvedMediaV1.meta.registryDoc
 * (added in Patch 2). This function safely reads and normalizes it.
 */
export function getPodcastRenderHintsFromResolved(
  resolved: ResolvedMediaV1,
  opts?: { targetLUFS?: number; keyHint?: string },
): PodcastAudioRenderHintsV1 {
  const targetLUFS = Number.isFinite(opts?.targetLUFS) ? Number(opts?.targetLUFS) : -16;

  const m = (resolved.meta || {}) as any;
  const registryDoc = (m.registryDoc || null) as RegistryDocMetaSlot;

  const keyHint =
    opts?.keyHint ||
    (typeof registryDoc?.key === "string" ? registryDoc.key : "") ||
    (typeof (resolved.ref as any)?.key === "string" ? String((resolved.ref as any).key) : "");

  const rawMeta = (registryDoc && typeof registryDoc === "object" ? (registryDoc as any).meta : null) ?? null;
  const meta = normalizePodcastAudioMetaV1(rawMeta, { keyHint });

  const { gain, gainDbTotal } = computeGainFromMeta(meta, targetLUFS);

  const fadeInSec = meta.fadeInMs ? meta.fadeInMs / 1000 : 0;
  const fadeOutSec = meta.fadeOutMs ? meta.fadeOutMs / 1000 : 0;

  return {
    role: meta.role,
    loop: meta.loop,
    gain,
    fadeInSec,
    fadeOutSec,
    tags: meta.tags ? [...meta.tags] : undefined,
    targetLUFS,
    declaredLUFS: meta.loudnessLUFS,
    gainDbTotal,
  };
}
